<script setup>
import { computed, ref, watch } from 'vue';
import { useEventListener } from '@vueuse/core';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { formatTime } from '@chatwoot/utils';
import { useAlert } from 'dashboard/composables';
import ReportsAPI from 'dashboard/api/reports';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import SidePanel from 'dashboard/components-next/side-panel/SidePanel.vue';
import { useReportDrilldown } from '../composables/useReportDrilldown';
import ReportDrilldownCard from './ReportDrilldownCard.vue';

const props = defineProps({
  open: { type: Boolean, default: false },
  metric: { type: String, default: '' },
  metricName: { type: String, default: '' },
  bucketLabel: { type: String, default: '' },
  bucketTimestamp: { type: Number, default: null },
  from: { type: Number, default: null },
  to: { type: Number, default: null },
  type: { type: String, default: 'account' },
  id: { type: [String, Number], default: null },
  groupBy: { type: String, default: '' },
  businessHours: { type: Boolean, default: false },
  bucketValue: { type: Number, default: null },
  isAverageMetric: { type: Boolean, default: false },
  canPrev: { type: Boolean, default: false },
  canNext: { type: Boolean, default: false },
});

const emit = defineEmits(['close', 'navigate']);

// The conversation list gets the bar as a list of display ids in the URL. A new tab
// sends the URL to the server, so the list stays short: one page of the API at most.
const VIEW_ALL_LIMIT = 100;

const panelRef = ref(null);
const isOpeningConversations = ref(false);
const { t } = useI18n();
const router = useRouter();
const {
  records,
  meta,
  isFetching,
  isFetchingMore,
  hasError,
  hasRecords,
  hasMore,
  open: openDrilldown,
  close,
  loadMore,
} = useReportDrilldown();

const title = computed(() => props.metricName || '');

const bucketValue = computed(() => {
  if (props.bucketValue === null) return '';

  return props.isAverageMetric
    ? formatTime(props.bucketValue)
    : `${props.bucketValue}`;
});

// An average bar shows a duration, so the count of the rows behind it adds context.
const resultCount = computed(() => {
  if (!props.isAverageMetric || !meta.value.total_count) return '';

  const count = meta.value.total_count;
  return meta.value.record_type === 'message'
    ? t('REPORT.DRILLDOWN.RESULT_COUNT_MESSAGE', { count })
    : t('REPORT.DRILLDOWN.RESULT_COUNT_CONVERSATION', { count });
});

const subtitle = computed(() =>
  [props.bucketLabel, resultCount.value].filter(Boolean).join(' ⋅ ')
);

const hiddenCount = computed(() => meta.value.hidden_count || 0);

const request = computed(() => ({
  metric: props.metric,
  bucketTimestamp: props.bucketTimestamp,
  from: props.from,
  to: props.to,
  type: props.type,
  id: props.id,
  groupBy: props.groupBy,
  businessHours: props.businessHours,
}));

const showViewAll = computed(
  () => props.metric === 'conversations_count' && meta.value.total_count > 0
);
const canViewAll = computed(() => meta.value.total_count <= VIEW_ALL_LIMIT);
const viewAllTooltip = computed(() =>
  canViewAll.value
    ? ''
    : t('REPORT.DRILLDOWN.VIEW_ALL_LIMIT', { limit: VIEW_ALL_LIMIT })
);

const conversationListHref = displayIds => {
  const { href } = router.resolve({
    name: 'home',
    query: { display_ids: [...new Set(displayIds)].join(',') },
  });
  return href;
};

const displayIdsOf = list =>
  list.map(record => record.conversation?.display_id).filter(Boolean);

const viewAllConversations = async () => {
  if (!canViewAll.value || isOpeningConversations.value) return;

  if (records.value.length >= meta.value.total_count) {
    window.open(
      conversationListHref(displayIdsOf(records.value)),
      '_blank',
      'noopener'
    );
    return;
  }

  // The tab opens before the request, while the click still allows a new tab.
  const tab = window.open('', '_blank');
  isOpeningConversations.value = true;
  try {
    const response = await ReportsAPI.getDrilldown({
      ...request.value,
      page: 1,
      perPage: VIEW_ALL_LIMIT,
    });
    const href = conversationListHref(displayIdsOf(response.data.payload));
    if (tab) {
      tab.opener = null;
      tab.location.href = href;
    }
  } catch {
    tab?.close();
    useAlert(t('REPORT.DRILLDOWN.ERROR'));
  } finally {
    isOpeningConversations.value = false;
  }
};

const recordKey = record =>
  `${record.record_type}-${record.message?.id || record.conversation?.id}-${
    record.occurred_at
  }`;

const fetchDrilldown = () => {
  openDrilldown(request.value);
};

const navigate = direction => {
  if (direction < 0 && !props.canPrev) return;
  if (direction > 0 && !props.canNext) return;

  emit('navigate', direction);
};

const onKeydown = event => {
  if (!props.open) return;

  if (event.key === 'ArrowLeft') {
    navigate(-1);
  } else if (event.key === 'ArrowRight') {
    navigate(1);
  }
};

useEventListener(document, 'keydown', onKeydown);

watch(
  () => props.open,
  isDrawerOpen => {
    if (!isDrawerOpen) {
      panelRef.value?.close();
      close();
      return;
    }

    panelRef.value?.open();
    fetchDrilldown();
  }
);

watch(
  () => [props.metric, props.bucketTimestamp],
  () => {
    if (props.open) fetchDrilldown();
  }
);
</script>

<template>
  <SidePanel ref="panelRef" :title="title" width="xl" @close="emit('close')">
    <template #header>
      <div class="min-w-0">
        <h3 class="truncate text-base font-medium text-n-slate-12">
          {{ title }}
        </h3>
        <p
          v-if="bucketValue"
          class="mt-1 text-xl font-semibold text-n-slate-12"
        >
          {{ bucketValue }}
        </p>
        <div
          class="text-sm text-n-slate-11"
          :class="{
            'mt-2': bucketValue,
            'mt-1': !bucketValue,
          }"
        >
          {{ subtitle }}
        </div>
      </div>
    </template>
    <template #header-actions>
      <span v-if="showViewAll" v-tooltip.bottom="viewAllTooltip">
        <Button
          faded
          slate
          size="sm"
          icon="i-lucide-external-link"
          data-test-id="drilldown-view-all"
          :label="$t('REPORT.DRILLDOWN.VIEW_ALL')"
          :disabled="!canViewAll"
          :is-loading="isOpeningConversations"
          @click="viewAllConversations"
        />
      </span>
      <Button
        ghost
        slate
        size="sm"
        icon="i-ph-caret-left"
        class="rtl:rotate-180"
        :disabled="!canPrev"
        :aria-label="$t('REPORT.DRILLDOWN.PREVIOUS_BUCKET')"
        @click="navigate(-1)"
      />
      <Button
        ghost
        slate
        size="sm"
        icon="i-ph-caret-right"
        class="rtl:rotate-180"
        :disabled="!canNext"
        :aria-label="$t('REPORT.DRILLDOWN.NEXT_BUCKET')"
        @click="navigate(1)"
      />
    </template>
    <div v-if="isFetching" class="flex h-40 items-center justify-center">
      <Spinner />
    </div>

    <div
      v-else-if="hasError"
      class="flex h-40 items-center justify-center text-sm text-n-ruby-11"
    >
      {{ $t('REPORT.DRILLDOWN.ERROR') }}
    </div>

    <template v-else>
      <p
        v-if="hiddenCount"
        data-test-id="drilldown-hidden-count"
        class="mb-3 rounded-md bg-n-alpha-1 px-3 py-2 text-sm text-n-slate-11"
      >
        {{ $t('REPORT.DRILLDOWN.HIDDEN_COUNT', { count: hiddenCount }) }}
      </p>

      <div
        v-if="!hasRecords"
        class="flex h-40 items-center justify-center text-sm text-n-slate-10"
      >
        {{ $t('REPORT.DRILLDOWN.EMPTY') }}
      </div>

      <div v-else class="flex flex-col gap-2">
        <ReportDrilldownCard
          v-for="record in records"
          :key="recordKey(record)"
          :record="record"
        />

        <Button
          v-if="hasMore"
          faded
          slate
          size="sm"
          class="mx-auto mt-2"
          :label="$t('REPORT.DRILLDOWN.LOAD_MORE')"
          :is-loading="isFetchingMore"
          @click="loadMore"
        />
      </div>
    </template>
  </SidePanel>
</template>
